import { ChakraV3, Empty, FancyDate, ScrollArea } from '@piccolohealth/ui';
import { DateTime, P } from '@piccolohealth/util';
import React from 'react';
import { BiSolidBarChartAlt2 } from 'react-icons/bi';
import { ParticipantBehaviourRecordingTypeTag } from '../../../behaviours/components/ParticipantBehaviourRecordingTypeTag';
import { ParticipantBehaviourRecordingValue } from '../../../behaviours/components/ParticipantBehaviourRecordingValue';
import type { UseTableReturn } from '../../hooks/useTable';

interface Props {
  table: UseTableReturn;
}

export const TableWidgetContent = (props: Props) => {
  const { table } = props;

  const content = P.run(() => {
    if (P.isEmpty(table.recordings)) {
      return (
        <ChakraV3.Stack h='full' w='full' align='center' justify='center' textAlign='center'>
          <Empty
            title='No series added'
            description='Add series to view them in the table. Series are used to display data on the table.'
            icon={
              <ChakraV3.Icon size='2xl' color='gray.300'>
                <BiSolidBarChartAlt2 />
              </ChakraV3.Icon>
            }
          />
        </ChakraV3.Stack>
      );
    }

    const entriesGroupedByDate = P.groupBy(table.recordings, (recording) =>
      DateTime.fromISO(recording.timestamp.toString()).startOf('day').toISO(),
    );

    return (
      <ScrollArea>
        <ChakraV3.Accordion.Root
          multiple
          collapsible
          defaultValue={Object.keys(entriesGroupedByDate)}
        >
          {P.listify(entriesGroupedByDate, (recordings, timestamp) => (
            <ChakraV3.Accordion.Item key={timestamp} value={timestamp}>
              <ChakraV3.Accordion.ItemTrigger px={4} bg='gray.50'>
                <ChakraV3.HStack w='full'>
                  <FancyDate
                    date={DateTime.fromISO(timestamp)}
                    direction='row'
                    showHumanized={false}
                    showTooltip={true}
                    format={DateTime.DATE_SHORT}
                    fontSize='xs'
                    fontWeight='semibold'
                  />
                  <ChakraV3.Spacer />
                  <ChakraV3.Accordion.ItemIndicator />
                </ChakraV3.HStack>
              </ChakraV3.Accordion.ItemTrigger>
              <ChakraV3.Accordion.ItemContent px={4} py={3}>
                <ChakraV3.Stack>
                  {(recordings ?? []).map((recording) => {
                    const behaviour = table.participantBehaviours.find(
                      (behaviour) => behaviour.id === recording.participantBehaviourId,
                    );

                    return (
                      <ChakraV3.Stack key={recording.id} w='full' gap={0}>
                        <ChakraV3.HStack w='full'>
                          <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
                            {behaviour?.name}
                          </ChakraV3.Text>
                          <ChakraV3.Spacer />
                          <ParticipantBehaviourRecordingTypeTag
                            type={recording.__typename}
                            size='sm'
                          />
                        </ChakraV3.HStack>
                        <ChakraV3.Stack fontSize='xs'>
                          <ParticipantBehaviourRecordingValue recording={recording} />
                        </ChakraV3.Stack>
                      </ChakraV3.Stack>
                    );
                  })}
                </ChakraV3.Stack>
              </ChakraV3.Accordion.ItemContent>
            </ChakraV3.Accordion.Item>
          ))}
        </ChakraV3.Accordion.Root>
      </ScrollArea>
    );
  });

  return (
    <ChakraV3.Stack h='full' w='full' overflow='hidden' userSelect='none'>
      {content}
    </ChakraV3.Stack>
  );
};
